import React, { useEffect } from "react"
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  Path,
} from "react-hook-form"
import { useTranslation } from "react-i18next"

import { QuestionRenderer } from "components/questions/question-renderer"
import { FormLoadingSkeleton } from "components/questions/question-skeleton"
import { PlaceOrderFormData, Ticket, Question } from "types"
import { ReactComponent as Check } from "../../assets/check.svg"
import { detectUnifiedContactFields } from "utils/form-submission/contact-field-mapper"

interface AdvancedTicketCardProps {
  ticket: Ticket
  ticketIndex: number
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  watch: UseFormWatch<PlaceOrderFormData>
  setValue: UseFormSetValue<PlaceOrderFormData>
  isSameAsContact: boolean
  setIsSameAsContact: (value: boolean) => void
  isFirstTicketOfType: boolean
  questions?: Question[]
}

const AdvancedTicketCard: React.FC<AdvancedTicketCardProps> = ({
  ticket,
  ticketIndex,
  register,
  errors,
  watch,
  setValue,
  isSameAsContact,
  setIsSameAsContact,
  isFirstTicketOfType,
  questions: ticketQuestions,
}) => {
  const { t } = useTranslation()
  const questions = ticketQuestions || []
  const isLoading = !ticketQuestions
  const isFirstTicket = isFirstTicketOfType

  // Watch contact info fields
  const contactFirstName = watch("contactInfo.firstName")
  const contactLastName = watch("contactInfo.lastName")
  const contactEmail = watch("contactInfo.email")

  // Auto-populate first ticket fields when "same as contact" is checked
  useEffect(() => {
    if (!isSameAsContact || !isFirstTicket) return

    // Use unified contact field detection
    const mapping = detectUnifiedContactFields(
      questions,
      ticket.id,
      true // isAdvancedFormsEnabled
    )

    if (!mapping) return

    // Auto-populate contact fields using the unified mapping
    if (mapping.firstName && contactFirstName?.trim()) {
      setValue(
        mapping.firstName.fieldName as Path<PlaceOrderFormData>,
        contactFirstName.trim(),
        { shouldValidate: true }
      )
    }

    if (mapping.lastName && contactLastName?.trim()) {
      setValue(
        mapping.lastName.fieldName as Path<PlaceOrderFormData>,
        contactLastName.trim(),
        { shouldValidate: true }
      )
    }

    if (mapping.email && contactEmail?.trim()) {
      setValue(
        mapping.email.fieldName as Path<PlaceOrderFormData>,
        contactEmail.trim(),
        { shouldValidate: true }
      )
    }
  }, [isSameAsContact, contactFirstName, contactLastName, contactEmail, isFirstTicket, questions, ticket.id, setValue])

  return (
    <div
      key={ticket.id}
      className="overflow-visible p-4 bg-white rounded-lg border shadow-sm border-progress_bar_background text-default_card_text"
    >
      <div className="mb-4">
        <h3 className="mb-1 text-lg font-bold text-black">
          {ticket.ticket_name} #{ticketIndex + 1}
        </h3>

        {/* Same as contact information checkbox - only for first ticket */}
        {isFirstTicket && (
          <label className="flex items-center mb-4 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              onChange={(e) => setIsSameAsContact(e.target.checked)}
              checked={isSameAsContact}
            />
            <div
              className={`relative w-5 h-5 ${
                isSameAsContact
                  ? "bg-theme_accent"
                  : "bg-white border-2 border-input_border"
              } rounded transition mr-3`}
            >
              {isSameAsContact && (
                <Check
                  className="absolute top-1/2 left-1/2 w-3 h-3 transform -translate-x-1/2 -translate-y-1/2"
                  fill="white"
                />
              )}
            </div>
            <span className="text-theme_text">
              {t("details.contact_info_same")}
            </span>
          </label>
        )}
      </div>

      {/* Ticket-specific questions */}
      <div className="pb-4 space-y-4">
        {isLoading ? (
          <FormLoadingSkeleton />
        ) : (
          questions
            .sort((a: Question, b: Question) => a.rank - b.rank)
            .map((question: Question) => {
              return (
                <QuestionRenderer
                  key={`${ticket.id}-${question.uuid}`}
                  question={question}
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  ticketId={ticket.id}
                  isFirstTicket={isFirstTicket}
                  isSameAsContact={isSameAsContact}
                />
              )
            })
        )}
      </div>
    </div>
  )
}

export { AdvancedTicketCard }
